/**
 * Geocoding service using Nominatim (OpenStreetMap)
 * Free and no API key required
 */

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org'
const USER_AGENT = 'TrckIt Food Truck Tracker'

/**
 * Forward geocode an address to coordinates
 * @param {string} address - Address or location string to geocode
 * @param {string} countryCode - Country code to limit search (optional, null for global search)
 * @returns {Promise<Object|null>} - Location object with latitude, longitude, displayName
 */
export async function geocodeAddress(address, countryCode = null) {
  if (!address || !address.trim()) {
    throw new Error('Address is required for geocoding')
  }

  try {
    // Build URL with optional country code filter
    let url = `${NOMINATIM_BASE_URL}/search?format=json&q=${encodeURIComponent(address)}&limit=1`
    if (countryCode) {
      url += `&countrycodes=${countryCode}`
    }
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': USER_AGENT
      }
    })
    
    const data = await response.json()
    
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon),
        displayName: data[0].display_name
      }
    }
    
    return null
  } catch (error) {
    // Error is thrown and handled by caller
    throw error
  }
}

/**
 * Reverse geocode coordinates to address
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {number} zoom - Zoom level for detail (default: 18)
 * @returns {Promise<Object|null>} - Address object with formatted address components
 */
export async function reverseGeocode(latitude, longitude, zoom = 18) {
  if (!latitude || !longitude) {
    throw new Error('Latitude and longitude are required for reverse geocoding')
  }

  try {
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=${zoom}&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'en-US,en;q=0.9',
          'User-Agent': USER_AGENT
        }
      }
    )
    
    const data = await response.json()
    
    if (data && data.display_name) {
      // Format a clean address from the response
      const address = data.address
      let formattedAddress = ''
      
      if (address.road) {
        formattedAddress = address.road
      }
      if (address.city || address.town || address.village) {
        formattedAddress += formattedAddress ? ', ' : ''
        formattedAddress += address.city || address.town || address.village
      }
      if (address.state) {
        formattedAddress += formattedAddress ? ', ' : ''
        formattedAddress += address.state
      }
      
      return {
        formattedAddress: formattedAddress || data.display_name,
        fullAddress: data.display_name,
        addressComponents: address
      }
    }
    
    return null
  } catch (error) {
    // Error is thrown and handled by caller
    throw error
  }
}
